import React, { useState } from 'react';
import styled from 'styled-components';
import { useSermons } from '../hooks/useSermons';
import { Link } from 'react-router-dom';
import { useSermonAssets } from '../hooks/useSermonAssets';
import SermonNotesModal from '../components/SermonNotesModal';
import { useSetLiveSermonQuestionsEnabled, useToggleLiveSermon } from '../hooks/useToggleLiveSermon';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { Permission, hasAnyPermission } from '../utils/roles';
import { useDeleteSermonQuestion, useSermonQuestions, useUpdateSermonQuestionResponse, useUpdateSermonQuestionStatus } from '../hooks/useSermonQuestions';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  font-size: 1.05rem;
  margin: 0;
  color: var(--onSurface);
`;

const StatusChip = styled.span<{ $status: string | undefined }>`
  background-color: ${({ $status }) =>
    $status === 'published' ? 'var(--successContainer)' : 'var(--surfaceVariant)'};
  color: ${({ $status }) => ($status === 'published' ? 'var(--success)' : 'var(--onSurfaceVariant)')};
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.5rem;
`;

const Actions = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 0.75rem;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  position: absolute;
  right: 0.45rem;
  bottom: 0.95rem;
  z-index: 12;
`;

const MenuButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid var(--outline);
  border-radius: 999px;
  color: var(--onSurface);
  cursor: pointer;
  font-weight: 700;
  width: 30px;
  height: 30px;
  padding: 0;
  line-height: 1;
`;

const MenuPanel = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.4rem);
  min-width: 150px;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
  padding: 0.35rem;
  z-index: 20;
`;

const MenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  color: var(--onSurface);
  text-align: left;
  font-size: 0.82rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 0.5rem;
`;

const ThumbnailWrap = styled.div`
  position: relative;
`;

const EditIconLink = styled(Link)`
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: rgba(0, 0, 0, 0.6);
  color: var(--onSurface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.9rem;
`;

const AddButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.3);
`;

// New wrapper to align multiple add buttons
const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const InfoTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const Tag = styled.span`
  background: var(--surfaceVariant);
  color: var(--onSurfaceVariant);
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
`;

const LiveTag = styled(Tag)`
  background: var(--errorContainer);
  color: var(--error);
`;

const LiveButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? 'var(--error)' : 'var(--primary)')};
  color: var(--onPrimary);
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const QuestionsButton = styled.button`
  background: var(--surfaceVariant);
  color: var(--primary);
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 220;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
`;

const ModalCard = styled.div`
  width: min(620px, 95vw);
  max-height: 84vh;
  overflow: auto;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--outline);
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.4);
  padding: 1rem;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: var(--onSurface);
`;

const LiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--error);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const LivePulseDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--error);
  box-shadow: 0 0 0 0 rgba(207, 102, 121, 0.65);
  animation: question-live-pulse 1.6s ease-out infinite;

  @keyframes question-live-pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(207, 102, 121, 0.65);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 8px rgba(207, 102, 121, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(207, 102, 121, 0);
    }
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  cursor: pointer;
  font-size: 1.2rem;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--onSurface);
  margin-bottom: 0.9rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const SecondaryButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 1px solid var(--primary);
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

const QuestionCard = styled.div<{ $queued?: boolean }>`
  border: 1px solid ${({ $queued }) => ($queued ? 'var(--success)' : 'var(--outline)')};
  border-radius: 10px;
  background: ${({ $queued }) => ($queued ? 'var(--successContainer)' : 'var(--surfaceVariant)')};
  padding: 0.75rem;
  margin-bottom: 0.7rem;
`;

const QuestionMeta = styled.p`
  margin: 0 0 0.45rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
`;

const DeleteQuestionButton = styled.button`
  border: 1px solid var(--error);
  background: var(--errorContainer);
  color: var(--error);
  border-radius: 999px;
  width: 1.45rem;
  height: 1.45rem;
  line-height: 1;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const QuestionText = styled.p`
  margin: 0 0 0.6rem;
  color: var(--onSurface);
  white-space: pre-wrap;
`;

const QuestionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
`;

const ReplyModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1100;
`;

const ReplyModalCard = styled.div`
  width: min(100%, 28rem);
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
`;

const ReplyModalTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: var(--onSurface);
  font-size: 1.05rem;
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 6rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem;
  font: inherit;
  color: var(--onSurface);
  background: var(--background);
  resize: vertical;
  box-sizing: border-box;
`;

const ReplyModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const AdminResponseText = styled.p`
  margin: 0 0 0.6rem;
  color: #166534;
  white-space: pre-wrap;
  font-size: 0.9rem;
`;

type ModalMode = 'notes' | 'points' | 'confessions';

export const SermonsPage: React.FC = () => {
  const { data, isLoading, error } = useSermons();
  const toggleLive = useToggleLiveSermon();
  const setLiveQuestionsEnabled = useSetLiveSermonQuestionsEnabled();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>('notes');
  const [liveLoadingId, setLiveLoadingId] = useState<string | null>(null);
  const [questionToggleLoadingId, setQuestionToggleLoadingId] = useState<string | null>(null);
  const [openMenuSermonId, setOpenMenuSermonId] = useState<string | null>(null);
  const [expandedQueuedQuestionIds, setExpandedQueuedQuestionIds] = useState<Record<string, boolean>>({});
  const [pendingLiveSermon, setPendingLiveSermon] = useState<{ id: string; title: string } | null>(null);
  const [pendingQuestionsEnabled, setPendingQuestionsEnabled] = useState(true);
  const [questionsSermon, setQuestionsSermon] = useState<{ id: string; title: string } | null>(null);
  const [replyingQuestionId, setReplyingQuestionId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const { data: assets, isLoading: assetsLoading } = useSermonAssets(selectedId ?? undefined);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageContent = hasAnyPermission(roles, [Permission.MANAGE_CONTENT]);
  const canViewSermonQuestions = hasAnyPermission(roles, [Permission.VIEW_SERMON_QUESTIONS]);
  const canModerateQuestions = hasAnyPermission(roles, [Permission.MANAGE_SERMON_QUESTIONS]);
  const submittedQuestionsQuery = useSermonQuestions(
    questionsSermon
      ? { sermonId: questionsSermon.id, statuses: ['submitted', 'queued'] }
      : {}
  );
  const submittedQuestionsLiveQuery = useSermonQuestions({ statuses: ['submitted'] });
  const updateQuestionStatus = useUpdateSermonQuestionStatus();
  const updateQuestionResponse = useUpdateSermonQuestionResponse();
  const deleteQuestion = useDeleteSermonQuestion();

  const submittedCountBySermon = (submittedQuestionsLiveQuery.data ?? []).reduce<Record<string, number>>(
    (acc, question) => {
      acc[question.sermon_id] = (acc[question.sermon_id] ?? 0) + 1;
      return acc;
    },
    {}
  );

  const handleToggleLive = async (sermonId: string, makeLive: boolean) => {
    setLiveLoadingId(sermonId);
    try {
      await toggleLive.mutateAsync({ sermonId, makeLive });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to update live status.';
      alert(message);
    } finally {
      setLiveLoadingId(null);
    }
  };

  const handleConfirmStartLive = async () => {
    if (!pendingLiveSermon) return;

    setLiveLoadingId(pendingLiveSermon.id);
    try {
      await toggleLive.mutateAsync({
        sermonId: pendingLiveSermon.id,
        makeLive: true,
        questionsEnabled: pendingQuestionsEnabled,
      });
      setPendingLiveSermon(null);
      setPendingQuestionsEnabled(true);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to start live sermon.';
      alert(message);
    } finally {
      setLiveLoadingId(null);
    }
  };

  const handleQueueQuestion = async (questionId: string) => {
    try {
      await updateQuestionStatus.mutateAsync({
        questionId,
        nextStatus: 'queued',
        actorId: user?.id,
      });
      setExpandedQueuedQuestionIds((current) => ({
        ...current,
        [questionId]: false,
      }));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to queue question.';
      alert(message);
    }
  };

  const handleOpenReplyModal = (questionId: string, existingResponse?: string | null) => {
    setReplyingQuestionId(questionId);
    setReplyDraft(existingResponse ?? '');
  };

  const handleCloseReplyModal = () => {
    setReplyingQuestionId(null);
    setReplyDraft('');
  };

  const handleSaveReply = async () => {
    if (!replyingQuestionId) return;

    try {
      await updateQuestionResponse.mutateAsync({
        questionId: replyingQuestionId,
        adminResponse: replyDraft,
      });
      handleCloseReplyModal();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to save reply.';
      alert(message);
    }
  };

  const handleSetQuestionsEnabled = async (
    sermonId: string,
    sermonTitle: string,
    questionsEnabled: boolean
  ) => {
    setQuestionToggleLoadingId(sermonId);
    try {
      await setLiveQuestionsEnabled.mutateAsync({ sermonId, questionsEnabled });
      if (questionsEnabled) {
        setQuestionsSermon({ id: sermonId, title: sermonTitle });
      }
    } catch (e) {
      const message = e instanceof Error
        ? e.message
        : `Unable to ${questionsEnabled ? 'enable' : 'disable'} questions.`;
      alert(message);
    } finally {
      setQuestionToggleLoadingId(null);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    const confirmDelete = window.confirm('Delete this question?');
    if (!confirmDelete) return;

    try {
      await deleteQuestion.mutateAsync({ questionId });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to delete question.';
      alert(message);
    }
  };

  if (isLoading) return <p>Loading sermons…</p>;
  if (error) return <p>Error loading sermons</p>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Sermons</HeaderTitle>
        {canManageContent ? (
          <ButtonsWrapper>
            <AddButton to="/sermons/new">+ Add Sermon</AddButton>
            <AddButton to="/sermon-series/new">+ Add Series</AddButton>
          </ButtonsWrapper>
        ) : null}
      </HeaderRow>
      <CardsWrapper>
        {data?.map((s) => (
          <Card key={s.id}>
            <ThumbnailWrap>
              <Thumbnail src={s.thumbnail_url || '/sermon-fallback.jpg'} alt={s.title} />
                {canManageContent ? (
                  <EditIconLink to={`/sermons/${s.id}`} aria-label={`Edit ${s.title}`} title="Edit">
                    ✎
                  </EditIconLink>
                ) : null}
              <MenuWrap>
                <MenuButton
                  type="button"
                  aria-label="More sermon actions"
                  onClick={() => setOpenMenuSermonId((current) => (current === s.id ? null : s.id))}
                >
                  ...
                </MenuButton>
                {openMenuSermonId === s.id && (
                  <MenuPanel>
                    <MenuItem
                      type="button"
                      onClick={() => {
                        setSelectedId(s.id);
                        setModalMode('notes');
                        setOpenMenuSermonId(null);
                      }}
                    >
                      Notes
                    </MenuItem>
                    {(s.pointsCount ?? 0) > 0 && (
                      <MenuItem
                        type="button"
                        onClick={() => {
                          setSelectedId(s.id);
                          setModalMode('points');
                          setOpenMenuSermonId(null);
                        }}
                      >
                        Points ({s.pointsCount})
                      </MenuItem>
                    )}
                    {(s.confessionCount ?? 0) > 0 && (
                      <MenuItem
                        type="button"
                        onClick={() => {
                          setSelectedId(s.id);
                          setModalMode('confessions');
                          setOpenMenuSermonId(null);
                        }}
                      >
                        Confessions ({s.confessionCount})
                      </MenuItem>
                    )}
                  </MenuPanel>
                )}
              </MenuWrap>
            </ThumbnailWrap>
            <CardHeader>
              <Title>{s.title}</Title>
              <StatusChip $status={s.status}>{s.status ?? 'draft'}</StatusChip>
            </CardHeader>
            {s.series && <p style={{ margin: '0 0 0.25rem', color: 'var(--onSurfaceVariant)' }}><strong>Series:</strong> {s.series}</p>}
            {s.speaker && <p style={{ margin: '0 0 0.25rem', color: 'var(--onSurfaceVariant)' }}><strong>Speaker:</strong> {s.speaker}</p>}
            {s.subtitle && <p style={{ margin: '0 0 0.25rem', color: 'var(--onSurfaceVariant)' }}>{s.subtitle}</p>}
            <InfoTags>
              {s.is_live && <LiveTag>LIVE</LiveTag>}
              {s.notes && <Tag>Notes</Tag>}
              {s.confessionCount && s.confessionCount > 0 && <Tag>Conf {s.confessionCount}</Tag>}
              {(s.pointsCount ?? 0) > 0 && <Tag>Points {s.pointsCount}</Tag>}
            </InfoTags>
            <Footer>
              <span>{s.date ? new Date(s.date).toLocaleDateString() : '—'}</span>
            </Footer>
            <Actions>
              <ActionGroup>
                {canManageContent && (
                  <LiveButton
                    type="button"
                    $active={Boolean(s.is_live)}
                    disabled={toggleLive.isPending && liveLoadingId === s.id}
                    onClick={() => {
                      if (s.is_live) {
                        void handleToggleLive(s.id, false);
                        return;
                      }
                      setPendingLiveSermon({ id: s.id, title: s.title });
                      setPendingQuestionsEnabled(true);
                    }}
                  >
                    {toggleLive.isPending && liveLoadingId === s.id
                      ? 'Updating…'
                      : s.is_live
                        ? 'Stop Live'
                        : 'Go Live'}
                  </LiveButton>
                )}
                {canModerateQuestions && s.is_live && (
                  <QuestionsButton
                    type="button"
                    onClick={() => void handleSetQuestionsEnabled(s.id, s.title, !s.questions_enabled)}
                    disabled={questionToggleLoadingId === s.id}
                  >
                    {questionToggleLoadingId === s.id
                      ? (s.questions_enabled ? 'Disabling…' : 'Enabling…')
                      : (s.questions_enabled ? 'Turn Questions Off' : 'Turn Questions On')}
                  </QuestionsButton>
                )}
                {canModerateQuestions && s.is_live && (
                  <QuestionsButton
                    type="button"
                    onClick={() => setQuestionsSermon({ id: s.id, title: s.title })}
                  >
                    View Questions ({submittedCountBySermon[s.id] ?? 0})
                  </QuestionsButton>
                )}
                {!canModerateQuestions && canViewSermonQuestions && s.is_live && (
                  <QuestionsButton
                    type="button"
                    onClick={() => setQuestionsSermon({ id: s.id, title: s.title })}
                  >
                    View Questions ({submittedCountBySermon[s.id] ?? 0})
                  </QuestionsButton>
                )}
              </ActionGroup>
            </Actions>
          </Card>
        ))}
      </CardsWrapper>
      {selectedId && assets && (
        <SermonNotesModal
          title={modalMode === 'notes' ? 'Sermon Notes' : modalMode === 'points' ? 'Points Images' : 'Confession Images'}
          notes={modalMode==='notes'?assets.notes:undefined}
          images={modalMode==='notes'?[]: modalMode==='points'?assets.pointsImages:assets.confessionImages}
          showImageIndexPill={modalMode === 'points'}
          loading={assetsLoading}
          onClose={()=>setSelectedId(null)}
        />
      )}

      {pendingLiveSermon && (
        <ModalOverlay onClick={() => setPendingLiveSermon(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Start Live Sermon</ModalTitle>
              <CloseButton type="button" onClick={() => setPendingLiveSermon(null)}>×</CloseButton>
            </ModalHeader>
            <p style={{ marginTop: 0, color: 'var(--onSurfaceVariant)' }}>
              You are about to go live with <strong>{pendingLiveSermon.title}</strong>.
            </p>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={pendingQuestionsEnabled}
                onChange={(event) => setPendingQuestionsEnabled(event.target.checked)}
              />
              Enable questions for this live session
            </CheckboxLabel>
            <ModalActions>
              <SecondaryButton type="button" onClick={() => setPendingLiveSermon(null)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={() => void handleConfirmStartLive()}>
                Start Live
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {questionsSermon && (
        <ModalOverlay onClick={() => setQuestionsSermon(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                <ModalTitle>Submitted Questions · {questionsSermon.title}</ModalTitle>
                <LiveIndicator>
                  <LivePulseDot />
                  Live
                </LiveIndicator>
              </div>
              <CloseButton type="button" onClick={() => setQuestionsSermon(null)}>×</CloseButton>
            </ModalHeader>

            {submittedQuestionsQuery.isLoading ? (
              <p>Loading questions…</p>
            ) : (submittedQuestionsQuery.data?.length ?? 0) === 0 ? (
              <p style={{ color: 'var(--onSurfaceVariant)' }}>No questions yet.</p>
            ) : (
              submittedQuestionsQuery.data?.map((question) => (
                <QuestionCard key={question.id} $queued={question.status === 'queued'}>
                  <QuestionHeader>
                    <QuestionMeta>
                      {new Date(question.created_at).toLocaleString()} · {question.submitter_name || 'Anonymous'} · {question.submitter_source} · {question.status === 'queued' ? 'Queued' : 'Submitted'}
                    </QuestionMeta>
                    {canModerateQuestions ? (
                      <DeleteQuestionButton
                        type="button"
                        onClick={() => void handleDeleteQuestion(question.id)}
                        disabled={deleteQuestion.isPending}
                        aria-label="Delete question"
                        title="Delete question"
                      >
                        ×
                      </DeleteQuestionButton>
                    ) : null}
                  </QuestionHeader>

                  {(question.status !== 'queued' || expandedQueuedQuestionIds[question.id]) && (
                    <>
                      <QuestionText>{question.question_text}</QuestionText>
                      {question.admin_response ? (
                        <AdminResponseText>
                          <strong>Your reply:</strong> {question.admin_response}
                        </AdminResponseText>
                      ) : null}
                    </>
                  )}

                  <QuestionActions>
                    {question.status === 'queued' ? (
                      <GhostButton
                        type="button"
                        onClick={() =>
                          setExpandedQueuedQuestionIds((current) => ({
                            ...current,
                            [question.id]: !current[question.id],
                          }))
                        }
                      >
                        {expandedQueuedQuestionIds[question.id] ? 'Collapse' : 'Expand'}
                      </GhostButton>
                    ) : canModerateQuestions ? (
                      <>
                        <GhostButton
                          type="button"
                          onClick={() => handleOpenReplyModal(question.id, question.admin_response)}
                          disabled={updateQuestionResponse.isPending}
                        >
                          {question.admin_response ? 'Edit Reply' : 'Reply'}
                        </GhostButton>
                        <PrimaryButton
                          type="button"
                          onClick={() => void handleQueueQuestion(question.id)}
                          disabled={updateQuestionStatus.isPending}
                        >
                          Queue
                        </PrimaryButton>
                      </>
                    ) : null}
                  </QuestionActions>
                </QuestionCard>
              ))
            )}
          </ModalCard>
        </ModalOverlay>
      )}

      {replyingQuestionId ? (
        <ReplyModalBackdrop onClick={handleCloseReplyModal}>
          <ReplyModalCard onClick={(event) => event.stopPropagation()}>
            <ReplyModalTitle>Reply to question</ReplyModalTitle>
            <ReplyTextarea
              value={replyDraft}
              onChange={(event) => setReplyDraft(event.target.value)}
              placeholder="Write a reply for the submitter (optional)"
            />
            <ReplyModalActions>
              <GhostButton type="button" onClick={handleCloseReplyModal}>
                Cancel
              </GhostButton>
              <PrimaryButton
                type="button"
                onClick={() => void handleSaveReply()}
                disabled={updateQuestionResponse.isPending}
              >
                {updateQuestionResponse.isPending ? 'Saving…' : 'Save Reply'}
              </PrimaryButton>
            </ReplyModalActions>
          </ReplyModalCard>
        </ReplyModalBackdrop>
      ) : null}
    </Page>
  );
};

export default SermonsPage;
